import { ROLES } from "../../utils/hooks/useUserRole";
import {
  Branch,
  UserCreateRequest,
  UserDetailResponse,
  UserUpdateRequest,
} from "../../types/user.type";

export interface UserForm {
  username: string;
  firstName: string;
  lastName: string;
  email: string;
  roles: string[];
  branch_uuid: string;
  routeCode: string;
  full_name: string;
  id_number: string;
  phone: string;
}

export type UserFormErrors = Partial<Record<keyof UserForm, string>>;

export const INITIAL_USER_FORM: UserForm = {
  username: "",
  firstName: "",
  lastName: "",
  email: "",
  roles: [],
  branch_uuid: "",
  routeCode: "",
  full_name: "",
  id_number: "",
  phone: "",
};

export const ID_NUMBER_LENGTH = 13;
const EMAIL_PATTERN = /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/;
const ID_NUMBER_PATTERN = new RegExp(`^\\d{${ID_NUMBER_LENGTH}}$`);
// Same mobile / landline shapes the API accepts for a staff phone.
const PHONE_PATTERN = /^(08|09|06)\d{8}$|^(02|03|04|05|07)\d{7}$/;

export const isSalesPerson = (roles: string[]): boolean =>
  roles.includes(ROLES.BRANCH_SALES_PERSON);

/**
 * A sales person needs a staff row. The form asks for its details whenever the account has none yet:
 * always on create, and on edit only when no staff row is linked to the username.
 */
export const needsStaffDetails = (form: UserForm, hasStaff: boolean): boolean =>
  isSalesPerson(form.roles) && !hasStaff;

export function validateUserForm(
  form: UserForm,
  requireStaffDetails: boolean,
): UserFormErrors {
  const errors: UserFormErrors = {};

  if (!form.username.trim()) errors.username = "กรุณากรอกชื่อผู้ใช้";
  if (!form.firstName.trim()) errors.firstName = "กรุณากรอกชื่อจริง";
  if (!form.lastName.trim()) errors.lastName = "กรุณากรอกนามสกุล";
  if (form.email.trim() && !EMAIL_PATTERN.test(form.email.trim())) {
    errors.email = "รูปแบบอีเมล์ไม่ถูกต้อง";
  }
  if (!form.branch_uuid) errors.branch_uuid = "กรุณาเลือกสาขา";
  if (form.roles.length === 0) errors.roles = "กรุณาเลือกตำแหน่ง";
  if (isSalesPerson(form.roles) && !form.routeCode) {
    errors.routeCode = "กรุณาเลือกสายของพนักงานขาย";
  }
  if (!requireStaffDetails) return errors;

  if (!form.full_name.trim()) errors.full_name = "กรุณากรอกชื่อ-นามสกุลพนักงาน";
  if (!ID_NUMBER_PATTERN.test(form.id_number)) {
    errors.id_number = `เลขบัตรประชาชนต้องเป็นตัวเลข ${ID_NUMBER_LENGTH} หลัก`;
  }
  if (form.phone && !PHONE_PATTERN.test(form.phone)) {
    errors.phone = "รูปแบบเบอร์โทรไม่ถูกต้อง";
  }
  return errors;
}

export function toUserUpdateRequest(
  form: UserForm,
  requireStaffDetails: boolean,
): UserUpdateRequest {
  const request: UserUpdateRequest = {
    firstName: form.firstName.trim(),
    lastName: form.lastName.trim(),
    email: form.email.trim(),
    roles: form.roles,
    branch_uuid: form.branch_uuid,
    // Only a sales person carries a route; leaving it out clears a route a former role left behind.
    routeCode: isSalesPerson(form.roles) ? form.routeCode : undefined,
  };
  if (!requireStaffDetails) return request;
  return {
    ...request,
    full_name: form.full_name.trim(),
    id_number: form.id_number,
    phone: form.phone,
  };
}

export function toUserCreateRequest(
  form: UserForm,
  requireStaffDetails: boolean,
): UserCreateRequest {
  return {
    ...toUserUpdateRequest(form, requireStaffDetails),
    username: form.username.trim(),
  };
}

/** The edit form for a loaded user; the branch is matched by code, the attribute Keycloak stores. */
export function toUserForm(user: UserDetailResponse, branches: Branch[]): UserForm {
  const branch = branches.find((item) => item.branch_code === user.branchCode);
  return {
    ...INITIAL_USER_FORM,
    username: user.username,
    firstName: user.firstName,
    lastName: user.lastName,
    email: user.email ?? "",
    roles: user.roles,
    branch_uuid: branch?.uuid ?? "",
    routeCode: user.routeCode ?? "",
  };
}
